import { Grid } from "@mantine/core";
import dayjs from "dayjs";
import { useState } from "react";
import ReactApexChart from "react-apexcharts";
import useSWR from "swr";
import AdminKpiCard from "../AdminKpiCard/AdminKpiCard";
import AdminRankList from "../AdminRankList/AdminRankList";
import AdminInlineFilters from "../AdminInlineFilters/AdminInlineFilters";
import classes from "./AdminDashboardView.module.css";
import {
  augmentDashboardQueryString,
  buildDashboardSectionQueryString,
  buildDashboardSummaryQueryString,
  dashboardSwrKey,
} from "../../utils/dashboardQueryKeys";
import { DASHBOARD_EMPTY_STATE } from "../../constants";
import { fetcher } from "../../../../services/fetcher";

const FONT = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";
// Overview-specific palette: blue + teal for trend, indigo for operators, amber for plans
const TREND_COLORS = ["#2563eb", "#f59e0b"];
const METRIC_PARAM_MAP = { Revenue: "revenue", Bookings: "bookings", "Conversion Rate": "conversion_rate" };
const OPERATOR_BAR_COLOR = "#4f46e5";
const PLAN_BAR_COLOR = "#f97316";
const chartBase = {
  chart: { toolbar: { show: false }, animations: { enabled: false }, fontFamily: FONT },
  grid: { borderColor: "#eef0f4", strokeDashArray: 4, padding: { left: 2, right: 8 } },
  dataLabels: { enabled: false },
  stroke: { width: 2 },
  tooltip: { theme: "light", style: { fontSize: "12px", fontFamily: FONT } },
  legend: { fontSize: "12px", fontFamily: FONT, offsetY: 2 },
};

const truncate = (s, n = 22) => { const str = String(s ?? ""); return str.length > n ? `${str.substring(0, n)}…` : str; };

const AdminOverviewTab = ({
  filters,
  localFilters,
  setLocalFilters,
  timeFrame = "M",
  scope = "both",
}) => {
  const [bookingTrendDates, setBookingTrendDates] = useState({ startDate: null, endDate: null });
  const [planContribDates, setPlanContribDates] = useState({ startDate: null, endDate: null });

  const summaryCanon = buildDashboardSummaryQueryString({ timeFrame, scope, filters });
  const kpisUrl = dashboardSwrKey("dashboard/kpis/", summaryCanon);
  const bookingsTrendUrl = dashboardSwrKey(
    "dashboard/bookings-trend/",
    buildDashboardSectionQueryString(summaryCanon, bookingTrendDates)
  );
  const policyUrl = dashboardSwrKey("dashboard/policy-status-dashboard/", summaryCanon);
  const travellersUrl = dashboardSwrKey("dashboard/travellers-by-age-group-gender/", summaryCanon);
  const topOperatorsUrl = dashboardSwrKey("dashboard/top-operators-contribution/", summaryCanon);
  const topPlansUrl = dashboardSwrKey(
    "dashboard/top-plans-contribution/",
    buildDashboardSectionQueryString(summaryCanon, planContribDates)
  );
  const salesAgentsUrl = (() => {
    const metric = METRIC_PARAM_MAP[localFilters.agents];
    const q = augmentDashboardQueryString(summaryCanon, metric ? { sort_by: metric } : {});
    return dashboardSwrKey("dashboard/top-sales-agents-metrics/", q);
  })();
  const issuanceUrl = (() => {
    const q = augmentDashboardQueryString(summaryCanon, { sort_by: "revenue" });
    return dashboardSwrKey("dashboard/top-issuance-representatives/", q);
  })();

  const { data: kpisData } = useSWR(kpisUrl, fetcher);
  const { data: bookingsTrendData } = useSWR(bookingsTrendUrl, fetcher);
  const { data: policyStatusData } = useSWR(policyUrl, fetcher);
  const { data: travellersData } = useSWR(travellersUrl, fetcher);
  const { data: topOperatorsData } = useSWR(topOperatorsUrl, fetcher);
  const { data: topPlansData } = useSWR(topPlansUrl, fetcher);
  const { data: salesAgentsData } = useSWR(salesAgentsUrl, fetcher);
  const { data: issuanceData } = useSWR(issuanceUrl, fetcher);

  const pickMetricValue = (item, metric) => {
    if (metric === "Bookings")
      return item?.total_bookings ?? item?.bookings ?? item?.issuances ?? item?.total_issuances ?? item?.policies_issued ?? item?.value ?? 0;
    if (metric === "Conversion Rate")
      return item?.conversion_rate ?? item?.leads_won ?? item?.conversion_percent ?? item?.value ?? 0;
    return item?.revenue ?? item?.total_revenue ?? item?.contribution_value ?? item?.amount ?? item?.value ?? 0;
  };

  const resolveName = (item) =>
    item?.rep_name ||
    item?.sales_agent_name ||
    item?.agent_name ||
    item?.representative_name ||
    item?.issuance_representative_name ||
    item?.full_name ||
    item?.user_name ||
    item?.username ||
    item?.name ||
    (item?.first_name ? `${item.first_name} ${item.last_name ?? ""}`.trim() : null) ||
    item?.user?.full_name ||
    item?.user?.username ||
    null;

  const mapRankItems = (rows, metric = "Revenue") =>
    (Array.isArray(rows) ? rows : []).map((item) => ({
      name: resolveName(item) ?? "—",
      value: pickMetricValue(item, metric),
    }));

  // ── Booking trend (must be before dynamicKpis for sparkData) ─────────────────
  const bookingsPoints = (() => {
    const directRows =
      Array.isArray(bookingsTrendData?.points) ? bookingsTrendData.points
        : Array.isArray(bookingsTrendData?.weeks) ? bookingsTrendData.weeks
        : Array.isArray(bookingsTrendData?.months) ? bookingsTrendData.months
        : Array.isArray(bookingsTrendData?.data?.points) ? bookingsTrendData.data.points
        : Array.isArray(bookingsTrendData?.data?.weeks) ? bookingsTrendData.data.weeks
        : Array.isArray(bookingsTrendData?.data?.months) ? bookingsTrendData.data.months
        : Array.isArray(bookingsTrendData?.bookings_trend) ? bookingsTrendData.bookings_trend
        : Array.isArray(bookingsTrendData?.trend) ? bookingsTrendData.trend
        : Array.isArray(bookingsTrendData?.data) ? bookingsTrendData.data
        : (Array.isArray(bookingsTrendData) ? bookingsTrendData : []);

    if (directRows.length) return directRows;

    // Fallback for contract shapes using parallel label/value arrays
    const labels = bookingsTrendData?.labels ?? bookingsTrendData?.data?.labels ?? [];
    const values = bookingsTrendData?.values ?? bookingsTrendData?.data?.values ?? [];
    const conv = bookingsTrendData?.conversion_values ?? bookingsTrendData?.data?.conversion_values ?? [];
    if (Array.isArray(labels) && Array.isArray(values) && labels.length === values.length) {
      return labels.map((label, idx) => ({
        label,
        bookings: Number(values[idx] ?? 0),
        conversion_rate: Number(conv[idx] ?? 0),
      }));
    }
    return [];
  })();

  // Fill missing dates with zero values so the chart has no gaps
  const filledPoints = (() => {
    if (!bookingsPoints.length || timeFrame === "W") return bookingsPoints;
    const first = bookingsPoints[0]?.label ?? bookingsPoints[0]?.date ?? "";
    const isDaily = /^\d{4}-\d{2}-\d{2}$/.test(first);
    const isMonthly = /^\d{4}-\d{2}$/.test(first);
    if (!isDaily && !isMonthly) return bookingsPoints;
    const unit = isMonthly || timeFrame === "M" ? "month" : "day";
    const fmt = unit === "month" ? "YYYY-MM" : "YYYY-MM-DD";
    const ptMap = new Map(bookingsPoints.map((p) => [p?.label ?? p?.date ?? p?.period ?? "", p]));
    const start = filters?.startDate ? dayjs(filters.startDate) : dayjs(first);
    const end = filters?.endDate ? dayjs(filters.endDate) : dayjs(bookingsPoints[bookingsPoints.length - 1]?.label ?? bookingsPoints[bookingsPoints.length - 1]?.date ?? first);
    const filled = [];
    let cur = start.startOf(unit);
    let guard = 0;
    while (!cur.isAfter(end) && guard++ < 400) {
      const key = cur.format(fmt);
      filled.push(ptMap.get(key) ?? { label: key, bookings: 0, conversion_rate: 0 });
      cur = cur.add(1, unit);
    }
    return filled.length ? filled : bookingsPoints;
  })();

  // Auto-aggregate daily data when point count > 20 to reduce visual clutter
  const displayPoints = (() => {
    if (timeFrame !== "D" || filledPoints.length <= 20) return filledPoints;
    const bkt = Math.ceil(filledPoints.length / 20);
    const result = [];
    for (let i = 0; i < filledPoints.length; i += bkt) {
      const chunk = filledPoints.slice(i, i + bkt);
      const convChunk = chunk.filter((p) => Number(p?.conversion_rate ?? p?.conv_rate ?? 0) > 0);
      result.push({
        label: chunk[0]?.label ?? chunk[0]?.date ?? "",
        bookings: chunk.reduce((s, p) => s + Number(p?.bookings ?? p?.total_bookings ?? 0), 0),
        conversion_rate: convChunk.length
          ? convChunk.reduce((s, p) => s + Number(p?.conversion_rate ?? p?.conv_rate ?? 0), 0) / convChunk.length
          : 0,
      });
    }
    return result;
  })();

  const bookingCategories = displayPoints.map((p) => p?.week_label ?? p?.month_label ?? p?.label ?? p?.date ?? p?.period ?? p?.week ?? p?.month ?? "");
  const bookingValues = displayPoints.map((p) => Number(p?.bookings ?? p?.total_bookings ?? p?.booking_count ?? p?.count ?? 0));
  const convValues = displayPoints.map((p) => Number(p?.conversion_rate ?? p?.conv_rate ?? p?.conversion ?? 0));
  const bookingAxisCategories = bookingCategories.map((label, idx) => (
    timeFrame === "W" ? `W${idx + 1}` : timeFrame === "M" ? `M${idx + 1}` : label
  ));

  // ── KPI derivation ───────────────────────────────────────────────────────────
  const dynamicKpis = [
    {
      label: "Bookings",
      value: kpisData?.bookings?.value != null ? String(kpisData.bookings.value) : "—",
      tone: "blue",
      sparkData: bookingValues,
    },
    {
      label: "Leads Won",
      value: kpisData?.conversion_rate?.leads_won != null ? String(kpisData.conversion_rate.leads_won) : "—",
      tone: "amber",
      sparkData: convValues,
    },
    {
      label: "Conv. Rate",
      value:
        kpisData?.conversion_rate?.value != null
          ? `${Number(kpisData.conversion_rate.value).toFixed(2)}%`
          : "—",
      tone: "green",
    },
  ];


  // ── Policy status ─────────────────────────────────────────────────────────────
  const ps =
    policyStatusData?.policy_status ??
    policyStatusData?.data?.policy_status ??
    policyStatusData?.data ??
    policyStatusData ?? {};
  const statusKeys = ["active", "yet_to_active", "matured", "pending"];
  const statusLabels = ["Active", "Yet to Active", "Matured", "Pending"];
  // Support both nested {active: {domestic: X, international: Y}} and flat {active: N}
  const policyDomestic = statusKeys.map((k) => {
    const v = ps?.[k];
    if (v == null) return 0;
    return typeof v === "object" ? Number(v?.domestic ?? v?.dom ?? v?.total ?? 0) : Number(v);
  });
  const policyInternational = statusKeys.map((k) => {
    const v = ps?.[k];
    return typeof v === "object" ? Number(v?.international ?? v?.intl ?? 0) : 0;
  });

  // ── Travellers by age/gender — handles both array and object response shapes ──
  const ageRaw =
    travellersData?.age_groups ??
    travellersData?.data?.age_groups ??
    travellersData?.age_group_data ??
    travellersData?.data ??
    null;
  let ageCategories, maleSeries, femaleSeries;
  if (Array.isArray(ageRaw)) {
    ageCategories = ageRaw.map((r) => r?.age_group ?? r?.range ?? r?.group ?? r?.label ?? "");
    maleSeries = ageRaw.map((r) => Number(r?.male ?? r?.male_count ?? 0));
    femaleSeries = ageRaw.map((r) => Number(r?.female ?? r?.female_count ?? 0));
  } else if (ageRaw && typeof ageRaw === "object") {
    ageCategories = Object.keys(ageRaw);
    maleSeries = ageCategories.map((k) => Number(ageRaw[k]?.male ?? ageRaw[k]?.male_count ?? 0));
    femaleSeries = ageCategories.map((k) => Number(ageRaw[k]?.female ?? ageRaw[k]?.female_count ?? 0));
  } else {
    ageCategories = [];
    maleSeries = [];
    femaleSeries = [];
  }

  // ── Top operators ─────────────────────────────────────────────────────────────
  const operatorRows =
    Array.isArray(topOperatorsData?.top_operators) ? topOperatorsData.top_operators
      : Array.isArray(topOperatorsData?.operators) ? topOperatorsData.operators
      : Array.isArray(topOperatorsData?.results) ? topOperatorsData.results
      : Array.isArray(topOperatorsData) ? topOperatorsData
      : [];
  const operatorCategories = operatorRows.slice(0, 8).map((item) => item.operator_name || item.name || "Operator");
  const operatorValues = operatorRows.slice(0, 8).map((item) =>
    Number(item.percentage ?? item.contribution_value ?? item.revenue ?? 0)
  );

  // ── Top plans (top-plans-contribution/) ───────────────────────────────────────
  const planRows =
    Array.isArray(topPlansData) ? topPlansData
      : Array.isArray(topPlansData?.top_plans) ? topPlansData.top_plans
      : Array.isArray(topPlansData?.plans) ? topPlansData.plans
      : Array.isArray(topPlansData?.plan_contributions) ? topPlansData.plan_contributions
      : Array.isArray(topPlansData?.results) ? topPlansData.results
      : Array.isArray(topPlansData?.data) ? topPlansData.data
      : [];
  const planChartRows = planRows.slice(0, 6);
  const planCategories = planChartRows.map((item) => item.plan_name || item.title || item.name || "Plan");
  const planValues = planChartRows.map((item) =>
    Number(
      item.percentage ?? item.contribution_value ?? item.contribution ?? item.revenue_percent
        ?? item.revenue ?? item.bookings ?? item.total_bookings ?? item.value ?? 0
    )
  );
  const hasPlanData = planChartRows.length > 0;
  const planChartHeight = Math.max(260, planChartRows.length * 42 + 20);

  // ── Rank lists ────────────────────────────────────────────────────────────────
  const salesAgentRows = mapRankItems(
    Array.isArray(salesAgentsData?.results) ? salesAgentsData.results : Array.isArray(salesAgentsData) ? salesAgentsData : [],
    localFilters.agents
  );
  const issuanceRows = mapRankItems(
    Array.isArray(issuanceData?.results) ? issuanceData.results : Array.isArray(issuanceData) ? issuanceData : [],
    "Revenue"
  );

  // ── Chart options ─────────────────────────────────────────────────────────────
  // View-aware label formatter: handles daily dates, "2026-W12" weekly, "2026-03" monthly
  const fmtDateLabel = (val) => {
    if (!val) return "";
    const s = String(val);
    const wMatch = s.match(/[Ww](\d+)/);
    if (wMatch) return `Wk ${wMatch[1]}`;
    const mMatch = s.match(/^(\d{4})-(\d{2})$/);
    if (mMatch) {
      const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return `${mo[parseInt(mMatch[2]) - 1]} '${mMatch[1].slice(2)}`;
    }
    const d = new Date(val);
    if (isNaN(d)) return s;
    if (timeFrame === "M") return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  };

  const isDaily = timeFrame === "D";
  const cats = bookingAxisCategories;
  // Interval tick count: dense daily view max 7, weekly 8, monthly 12
  const trendTickAmount = isDaily ? Math.min(cats.length, 7) : timeFrame === "W" ? Math.min(cats.length, 8) : Math.min(cats.length, 12);

  const maxConvValue = convValues.length ? Math.max(...convValues.map((v) => Number(v) || 0)) : 0;
  const convAxisMax = maxConvValue > 0 ? Math.min(100, Math.ceil(maxConvValue / 10) * 10 + 5) : 100;

  const dualYaxis = [
    {
      seriesName: "Bookings",
      title: { text: "Bookings", style: { fontSize: "10px", fontFamily: FONT } },
      labels: { style: { fontSize: "10px" }, formatter: (v) => Math.round(v) },
    },
    {
      seriesName: "Conv. Rate %",
      opposite: true,
      min: 0,
      max: convAxisMax,
      title: { text: "Conv %", style: { fontSize: "10px", fontFamily: FONT } },
      labels: { style: { fontSize: "10px" }, formatter: (v) => `${Number(v).toFixed(2)}%` },
    },
  ];

  const sharedXaxis = {
    categories: cats,
    tickAmount: trendTickAmount,
    labels: {
      rotate: isDaily ? -40 : -20,
      formatter: fmtDateLabel,
      style: { fontSize: "9px" },
      hideOverlappingLabels: true,
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  };

  const sharedTooltip = {
    shared: true,
    intersect: false,
    x: {
      formatter: (_val, { dataPointIndex }) => {
        const axisLabel = cats[dataPointIndex] ?? "";
        const sourceLabel = bookingCategories[dataPointIndex] ?? axisLabel;
        if (timeFrame === "W" || timeFrame === "M") return `${axisLabel} (${sourceLabel})`;
        return fmtDateLabel(sourceLabel);
      },
    },
    y: {
      formatter: (val, { seriesIndex }) =>
        seriesIndex === 1 ? `${Number(val).toFixed(2)}%` : String(Math.round(val)),
    },
  };

  // Daily: dual line (less dense than bars). Weekly/Monthly: bar+line combo.
  const bookingTrendOptions = isDaily
    ? {
        ...chartBase,
        chart: { ...chartBase.chart, type: "line" },
        colors: TREND_COLORS,
        stroke: { width: [2.5, 2], curve: "smooth", dashArray: [0, 4] },
        fill: {
          type: ["gradient", "none"],
          gradient: { shade: "light", type: "vertical", opacityFrom: 0.15, opacityTo: 0.01 },
        },
        markers: { size: bookingValues.length <= 12 ? 3 : 0 },
        xaxis: sharedXaxis,
        yaxis: dualYaxis,
        tooltip: sharedTooltip,
        legend: { position: "top", fontFamily: FONT },
      }
    : {
        ...chartBase,
        chart: { ...chartBase.chart, type: "bar" },
        colors: TREND_COLORS,
        plotOptions: { bar: { borderRadius: 3, columnWidth: timeFrame === "W" ? "55%" : "45%" } },
        stroke: { width: [0, 2.5], curve: "smooth" },
        fill: { opacity: [0.85, 1] },
        xaxis: sharedXaxis,
        yaxis: dualYaxis,
        tooltip: sharedTooltip,
        legend: { position: "top", fontFamily: FONT },
      };

  const hasPolicyData = policyDomestic.some((v) => Number(v) > 0) || policyInternational.some((v) => Number(v) > 0);
  const hasBookingTrendData = bookingValues.length > 0 || convValues.length > 0;

  const policyOptions = {
    ...chartBase,
    colors: ["#1d4ed8", "#0d9488"],
    plotOptions: { bar: { borderRadius: 4, columnWidth: "52%" } },
    xaxis: {
      categories: statusLabels,
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: { labels: { style: { fontSize: "10px" } } },
    legend: { position: "top", fontFamily: FONT },
  };

  const ageGenderOptions = {
    ...chartBase,
    chart: { ...chartBase.chart, type: "bar" },
    colors: ["#2563eb", "#db2777"],
    xaxis: {
      categories: ageCategories,
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: { labels: { style: { fontSize: "10px" } } },
    plotOptions: { bar: { columnWidth: "48%", borderRadius: 4 } },
    legend: { position: "top", fontFamily: FONT },
  };

  return (
    <div className={classes.tabStack}>
      {/* KPIs */}
      <Grid gutter={12}>
        {dynamicKpis.map((item) => (
          <Grid.Col span={{ base: 12, sm: 4, md: 3 }} key={item.label}>
            <AdminKpiCard {...item} />
          </Grid.Col>
        ))}
      </Grid>

      {/* Policy Status + Top Operator Contribution + Age/Gender */}
      <Grid gutter={12}>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Policy Status</div>
            {hasPolicyData ? (
              <ReactApexChart
                options={policyOptions}
                series={[
                  { name: "Domestic", data: policyDomestic },
                  { name: "International", data: policyInternational },
                ]}
                type="bar"
                height={220}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Top Operator Contribution</div>
            {operatorValues.length ? (
              <ReactApexChart
                options={{
                  ...chartBase,
                  chart: { ...chartBase.chart, type: "bar" },
                  plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
                  colors: [OPERATOR_BAR_COLOR],
                  xaxis: {
                    categories: operatorCategories,
                    tickAmount: 4,
                    labels: { show: false },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                  },
                  yaxis: {
                    labels: {
                      formatter: (v) => truncate(v, 20),
                      style: { fontSize: "10px", fontFamily: FONT },
                    },
                  },
                  tooltip: { y: { formatter: (v) => `${Number(v).toFixed(2)}%` } },
                }}
                series={[{ name: "Revenue %", data: operatorValues }]}
                type="bar"
                height={220}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Traveller by Age + Gender</div>
            {ageCategories.length ? (
              <ReactApexChart
                options={ageGenderOptions}
                series={[
                  { name: "Male", data: maleSeries },
                  { name: "Female", data: femaleSeries },
                ]}
                type="bar"
                height={220}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
      </Grid>

      {/* Large Booking Trend */}
      <Grid gutter={12}>
        <Grid.Col span={{ base: 12, md: 12 }}>
          <div className={classes.card}>
            <div className={classes.cardHeaderRow}>
              <span className={classes.cardHeader}>Booking Trend</span>
              <AdminInlineFilters
                fromDate={bookingTrendDates.startDate}
                toDate={bookingTrendDates.endDate}
                setFromDate={(v) => setBookingTrendDates((p) => ({ ...p, startDate: v }))}
                setToDate={(v) => setBookingTrendDates((p) => ({ ...p, endDate: v }))}
              />
            </div>
            {hasBookingTrendData ? (
              <ReactApexChart
                key={timeFrame}
                options={bookingTrendOptions}
                series={[
                  { name: "Bookings", type: isDaily ? "line" : "bar", data: bookingValues },
                  { name: "Conv. Rate %", type: "line", data: convValues },
                ]}
                type={isDaily ? "line" : "bar"}
                height={320}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
      </Grid>

      {/* Rank lists + Top Plan Contribution (horizontal) */}
      <Grid gutter={12}>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <AdminRankList
            title="Top 5 Sales Agents"
            items={salesAgentRows.slice(0, 5)}
            localFilters={["Revenue", "Conversion Rate", "Bookings"]}
            localValue={localFilters.agents}
            onLocalChange={(value) => setLocalFilters((p) => ({ ...p, agents: value }))}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <AdminRankList
            title="Top Issuance Representatives"
            items={issuanceRows.slice(0, 5)}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <div className={classes.card}>
            <div className={`${classes.cardHeaderRow} ${classes.planChartHead}`}>
              <span className={classes.cardHeader}>Top Plan Contribution</span>
              <AdminInlineFilters
                fromDate={planContribDates.startDate}
                toDate={planContribDates.endDate}
                setFromDate={(v) => setPlanContribDates((p) => ({ ...p, startDate: v }))}
                setToDate={(v) => setPlanContribDates((p) => ({ ...p, endDate: v }))}
              />
            </div>
            {hasPlanData ? (
              <ReactApexChart
                options={{
                  ...chartBase,
                  chart: { ...chartBase.chart, type: "bar" },
                  grid: { ...chartBase.grid, padding: { left: 0, right: 10, top: 0, bottom: 0 } },
                  plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
                  colors: [PLAN_BAR_COLOR],
                  xaxis: {
                    categories: planCategories,
                    labels: { show: false },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                  },
                  yaxis: {
                    labels: {
                      maxWidth: 150,
                      align: "left",
                      formatter: (v) => truncate(String(v), 28),
                      style: { fontSize: "9px", fontFamily: FONT, colors: "#64748b" },
                    },
                  },
                  tooltip: {
                    theme: "light",
                    style: { fontSize: "12px", fontFamily: FONT },
                    custom: ({ series, seriesIndex, dataPointIndex }) => {
                      const label = planCategories[dataPointIndex] || "Plan";
                      const val = series[seriesIndex]?.[dataPointIndex] ?? 0;
                      return (
                        `<div style="padding:8px 10px;font-family:${FONT};font-size:12px">` +
                        `<div style="font-weight:600;margin-bottom:4px;max-width:220px;white-space:normal">${label}</div>` +
                        `<div>${Number(val).toLocaleString("en-IN")}</div></div>`
                      );
                    },
                  },
                }}
                series={[{ name: "Contribution", data: planValues }]}
                type="bar"
                height={planChartHeight}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default AdminOverviewTab;
